/**
 * Builder fee encoding for OstiumTradingStorage.BuilderFee.
 *
 * Builders pass human-readable basis points (`feeBps`): e.g. `10` = 10 bps (0.1%),
 * `2.55` = 2.55 bps (0.0255%). The contract stores `feeBps × 10_000` (PRECISION_6
 * percent scale; max 500_000 = 50 bps = 0.5%).
 */

/** Maximum builder fee in basis points (50 bps = 0.5%). */
export const MAX_BUILDER_FEE_BPS = 50;

/** Smallest allowed `feeBps` step (0.01 bps). */
export const MIN_BUILDER_FEE_BPS_STEP = 0.01;

/** On-chain maximum (`MAX_BUILDER_FEE_PERCENT` with PRECISION_6). */
export const MAX_BUILDER_FEE_CONTRACT = 500_000;

const BPS_TO_CONTRACT = 10_000;

/** Convert builder-facing bps to the uint32 contract field. */
export function bpsToContractBuilderFee(feeBps: number): bigint {
  if (feeBps === 0) return 0n;
  return BigInt(Math.round(feeBps * BPS_TO_CONTRACT));
}

/** Returns an error message when invalid, otherwise `null`. */
export function validateBuilderFeeBps(feeBps: number): string | null {
  if (!Number.isFinite(feeBps)) {
    return 'builder.feeBps must be a finite number';
  }
  if (feeBps < 0 || feeBps > MAX_BUILDER_FEE_BPS) {
    return `builder.feeBps must be between 0 and ${MAX_BUILDER_FEE_BPS} (0%–0.5%)`;
  }
  const centiBps = Math.round(feeBps * 100);
  if (Math.abs(feeBps * 100 - centiBps) > 1e-6) {
    return `builder.feeBps must be a multiple of ${MIN_BUILDER_FEE_BPS_STEP} (0.01 bps)`;
  }
  if (bpsToContractBuilderFee(feeBps) > MAX_BUILDER_FEE_CONTRACT) {
    return 'builder.feeBps exceeds on-chain maximum (0.5%)';
  }
  return null;
}
